import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import Modal from '../Modal';
import AutoSizeTextarea from '../AutoSizeTextarea';

// Small text-only reply modal – opens when the supplier clicks Reply
// on a Needs Clarification document. Submits the supplier's response
// and flips the document back to Awaiting Review for the admin to
// re-decide. No file upload here – that's the Replace flow (which
// reuses the upload modal).
//
// Server-side: writes supplier_reply + supplier_reply_at, clears
// review_decision so the document leaves the Needs Clarification
// state. The admin's note (review_notes) and reason (review_reason)
// stay on the row for audit; if Fusion clarifies again, those fields
// get overwritten.
//
// Required: a non-empty reply with at least 10 characters of
// meaningful text. The submit button stays disabled until that's
// met. Mirrors the upload modal's validation discipline.

const MIN_REPLY_LENGTH = 10;

const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E89A2E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function ComplianceReplyModal({ document: docMeta, record, supplierId, onSuccess, onClose }) {
  const [reply, setReply] = useState('');
  const [state, setState] = useState('input'); // input | submitting | success | error
  const [errorMessage, setErrorMessage] = useState(null);

  const trimmed = reply.trim();
  const canSubmit = trimmed.length >= MIN_REPLY_LENGTH;

  useEffect(() => {
    if (state !== 'success') return;
    const t = setTimeout(() => onSuccess?.(), 2000);
    return () => clearTimeout(t);
  }, [state, onSuccess]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !supplierId || !docMeta?.key) return;
    setState('submitting');
    setErrorMessage(null);
    const { error } = await supabase
      .from('supplier_onboarding_documents')
      .update({
        supplier_reply: trimmed,
        supplier_reply_at: new Date().toISOString(),
        review_decision: null,
        updated_at: new Date().toISOString(),
      })
      .eq('supplier_id', supplierId)
      .eq('document_key', docMeta.key);
    if (error) {
      setErrorMessage(error.message || 'Could not send. Try again.');
      setState('error');
      return;
    }
    setState('success');
  }, [canSubmit, supplierId, docMeta?.key, trimmed]);

  const submitting = state === 'submitting';
  const success = state === 'success';
  const safeClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [submitting, onClose]);

  if (success) {
    return (
      <Modal open onClose={onSuccess} maxWidth={420} stripe={false} ariaLabel="Reply sent">
        <div className="px-7 py-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: 'rgba(232, 154, 46, 0.1)', border: '1.5px solid rgba(232, 154, 46, 0.4)' }}>
            <CheckIcon />
          </div>
          <h2 className="font-['Metropolis'] text-lg font-extrabold text-ink mb-2">Reply sent</h2>
          <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-relaxed max-w-[320px] mx-auto mb-5">
            Fusion 2K will look at your reply alongside the original {docMeta.label} and let you know.
          </p>
          <p className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A]">
            Closing in 2 seconds…
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={safeClose} maxWidth={460} stripe={false} ariaLabel={`Reply to Fusion 2K's request – ${docMeta.label}`}>
      <Modal.Header
        eyebrow="Reply to Fusion 2K's request"
        title={docMeta.label}
        sub={record?.review_notes || docMeta.description}
        onClose={submitting ? undefined : safeClose}
      />
      <Modal.Body>
        <div className="mb-1">
          <label
            htmlFor="compliance-reply-text"
            className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#51534A] mb-2"
          >
            Your response
          </label>
          <AutoSizeTextarea
            id="compliance-reply-text"
            value={reply}
            onChange={(e) => { setReply(e.target.value); if (state === 'error') setState('input'); }}
            disabled={submitting}
            minRows={5}
            placeholder="Address Fusion 2K's clarification request – they'll see this alongside the file you've already uploaded."
            className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#51534A] focus:outline-none focus:border-[#E89A2E] disabled:opacity-50" />
          <p className="font-['Metropolis'] text-[11px] mt-1.5 text-[#51534A]">
            {trimmed.length === 0
              ? `At least ${MIN_REPLY_LENGTH} characters.`
              : trimmed.length < MIN_REPLY_LENGTH
                ? `${MIN_REPLY_LENGTH - trimmed.length} more character${MIN_REPLY_LENGTH - trimmed.length === 1 ? '' : 's'} to send.`
                : `${trimmed.length} characters.`}
          </p>
        </div>
        {state === 'error' && errorMessage && (
          <div
            role="alert"
            className="mt-4 rounded-md px-3 py-2.5 font-['Metropolis'] text-[12px]"
            style={{ backgroundColor: 'rgba(224, 41, 10, 0.08)', border: '1px solid rgba(224, 41, 10, 0.35)', color: '#E0290A' }}
          >
            Couldn't send. {errorMessage}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={safeClose}
            disabled={submitting}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#131313] bg-[#E89A2E] border border-[#E89A2E] rounded-full px-5 py-2 inline-flex items-center gap-2 transition-[filter,opacity] duration-150 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100"
          >
            {submitting ? (<><Spinner /><span>Sending…</span></>) : <span>Send Reply</span>}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
